/**
 * @file 接口传输加解密（对齐 RuoYi-Vue-Plus / plus-ui 约定）
 * @description 请求体 AES 加密（随机 AES key，RSA 加密后放 `encrypt-key` 头）；响应体按同一约定解密。
 *
 * @whenToUse 开关 `VITE_APP_ENCRYPT` 开启时，由 `src/lib/client.ts` 拦截器调用。
 * @whenNotToUse 业务代码不应直接调用本模块。
 */

import CryptoJS from 'crypto-js'
import JSEncrypt from 'jsencrypt'

/** 传输 AES 密钥所在的请求/响应头 */
export const ENCRYPT_HEADER = 'encrypt-key'

/**
 * 随机生成 32 位 AES 密钥字符串。
 * @returns AES 密钥
 */
function generateRandomString() {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  for (let i = 0; i < 32; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length))
  }
  return result
}

/**
 * 随机生成 AES 密钥。
 * @returns AES 密钥（WordArray）
 */
export function generateAesKey() {
  return CryptoJS.enc.Utf8.parse(generateRandomString())
}

/**
 * WordArray 转 Base64 字符串。
 * @param str 待编码数据
 */
export function encryptBase64(str: CryptoJS.lib.WordArray) {
  return CryptoJS.enc.Base64.stringify(str)
}

/**
 * Base64 字符串解析为 WordArray。
 * @param str Base64 字符串
 */
export function decryptBase64(str: string) {
  return CryptoJS.enc.Base64.parse(str)
}

/**
 * AES 加密（ECB + PKCS7，与后端约定一致）。
 * @param message 明文
 * @param aesKey AES 密钥
 * @returns Base64 密文
 */
export function encryptWithAes(
  message: string,
  aesKey: CryptoJS.lib.WordArray,
) {
  return CryptoJS.AES.encrypt(message, aesKey, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString()
}

/**
 * AES 解密（ECB + PKCS7）。
 * @param message Base64 密文
 * @param aesKey AES 密钥
 * @returns 明文字符串
 */
export function decryptWithAes(
  message: string,
  aesKey: CryptoJS.lib.WordArray,
) {
  return CryptoJS.AES.decrypt(message, aesKey, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  }).toString(CryptoJS.enc.Utf8)
}

/**
 * RSA 公钥加密（用于加密 AES 密钥传输）。
 * @param txt 明文
 * @returns 密文；公钥不合法等失败场景返回 false
 */
export function rsaEncrypt(txt: string) {
  const encryptor = new JSEncrypt()
  encryptor.setPublicKey(import.meta.env.VITE_APP_RSA_PUBLIC_KEY)
  return encryptor.encrypt(txt)
}

/**
 * RSA 私钥解密（用于解密响应头里的 AES 密钥）。
 * @param txt 密文
 * @returns 明文；解密失败返回 false
 */
export function rsaDecrypt(txt: string) {
  const decryptor = new JSEncrypt()
  decryptor.setPrivateKey(import.meta.env.VITE_APP_RSA_PRIVATE_KEY)
  return decryptor.decrypt(txt)
}
